package io

import AppBuildConfig
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.toComposeImageBitmap
import androidx.compose.ui.unit.Density
import api.LanguageType
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.utils.io.core.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.jetbrains.compose.resources.decodeToSvgPainter
import org.jetbrains.skia.Image


class RepoFiles(language: StateFlow<LanguageType>) : ImplRepo(language) {

    private val cacheImages = mutableMapOf<Long, ImageBitmap>()
    private val cacheIcons = mutableMapOf<Long, Painter>()

    private val cacheStatics = mutableMapOf<String, ImageBitmap>()

    val fetchScope = CoroutineScope(Dispatchers.Default)

    companion object {
        private val mutexImageGlobal = Mutex()
        private val mutexImageMap = mutableMapOf<String, Mutex>()
        private val mutexIconGlobal = Mutex()
        private val mutexIconMap = mutableMapOf<String, Mutex>()
    }

    suspend fun fetchImageOrNull(id: Long): ImageBitmap? = runCatching {
        fetchImage(id)
    }.getOrNull()

    suspend fun fetchImage(id: Long): ImageBitmap {
        if (contains(id)) return cacheImages[id]!!

        return run {
            val itemMutex = mutexImageGlobal.withLock {
                mutexImageMap.getOrPut(id.toString()) {
                    Mutex()
                }
            }
            itemMutex.withLock {
                cacheImages[id]
            } ?: fetchScope.async {
                itemMutex.withLock {
                    runCatching {
                        client.rawCached().use { call ->
                            call.get(createUrlThumbnail(id))
                                .takeIf { it.status.isSuccess() }
                                ?.body<ByteArray>()
                                ?.let { Image.makeFromEncoded(it).toComposeImageBitmap() }
                                ?.also { it.prepareToDraw() }
                                ?.also { cacheImages[id] = it }
                                ?: error("fetch fail")
                        }
                    }
                }
            }.await().getOrThrow()
        }
    }

    suspend fun fetchImage(category: String, key: String): ImageBitmap {
        val hash = "${category}:${key}"
        if (contains(category, key)) return cacheStatics[hash]!!

        val itemMutex = mutexImageGlobal.withLock {
            mutexImageMap.getOrPut(hash) {
                Mutex()
            }
        }
        return itemMutex.withLock {
            cacheStatics[hash]
        } ?: fetchScope.async {
            cacheStatics[hash] ?: client.rawCached().use { call ->
                call.get(createUrlImageStatic(category, key))
                    .takeIf { it.status.isSuccess() }
                    ?.body<ByteArray>()
                    ?.let { Image.makeFromEncoded(it).toComposeImageBitmap() }
                    ?.also { cacheStatics[hash] = it }
                    ?: error("fetch fail")
            }
        }.await()
    }

    suspend fun fetchIcon(id: Long, density: Float = 1F): Painter {
        if (containsIcon(id)) return cacheIcons[id]!!

        return run {
            val itemMutex = mutexIconGlobal.withLock {
                mutexIconMap.getOrPut(id.toString()) {
                    Mutex()
                }
            }

            itemMutex.withLock {
                cacheIcons[id]
            } ?: fetchScope.async {
                itemMutex.withLock {
                    runCatching {
                        client.rawCached().use { call ->
                            call.get(createUrlIcon(id))
                                .takeIf { it.status.isSuccess() }
                                ?.body<ByteArray>()
                                ?.decodeToSvgPainter(Density(density))
                                ?.also { cacheIcons[id] = it }
                                ?: error("fetch fail")
                        }
                    }
                }
            }.await().getOrThrow()
        }
    }


    fun containsIcon(id: Long): Boolean = cacheIcons.contains(id)
    fun contains(id: Long): Boolean = cacheImages.contains(id)

    fun contains(category: String, key: String): Boolean = cacheStatics.contains("${category}:${key}")

    fun createUrlThumbnail(id: Long) = "${AppBuildConfig.HOST_FILES}/api/image/thumbnail/$id"
    fun createUrlImageOrigin(id: Long) = "${AppBuildConfig.HOST_FILES}/api/image/origin/$id"
    fun createUrlImageStatic(category: String, key: String) =
        "${AppBuildConfig.HOST_FILES}/api/image/static/$category/$key"

    fun createUrlVideoH265(id: Long) = "${AppBuildConfig.HOST_FILES}/api/video/H265/$id"
    fun createUrlAudio(id: Long) = "${AppBuildConfig.HOST_FILES}/api/audio/$id/download"
    fun createUrlVideoAlpha(id: Long) = "${AppBuildConfig.HOST_FILES}/api/alpha/video/$id"
    fun createUrlDocument(id: Long) = "${AppBuildConfig.HOST_FILES}/api/pdf/converted/$id"
    fun createUrlIcon(id: Long) = "${AppBuildConfig.URL_ICONS}/api/icon/$id/svg"
}
